import { useState } from "react";
import { FileDown, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

const COLLECTIONS = [
  { id: "users",        label: "Usuarios",          desc: "Demo + reales" },
  { id: "beta_signups", label: "Beta signups",      desc: "Lista de espera" },
  { id: "tracks",       label: "Tracks · Música",   desc: "Obras musicales y stats" },
  { id: "artists",      label: "Artistas",          desc: "Perfiles oficiales" },
  { id: "referrals",    label: "Referidos",         desc: "Cascada Golpe de Gracia" },
  { id: "sparks_users", label: "Sparks · Saldos",   desc: "Balance por usuario" },
  { id: "ads",          label: "Anuncios",          desc: "Campañas activas/pasadas" },
  { id: "reports",      label: "Reportes",          desc: "Moderación" },
];

export default function ExportTab() {
  const auth = useAdminAuth();
  const [busy, setBusy] = useState("");

  const dl = async (key, fmt) => {
    setBusy(`${key}-${fmt}`);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await auth.apiDownload(`/admin/export/${key}.${fmt}`, `ovamx_${key}_${stamp}.${fmt}`);
      toast.success(`Exportado ${key}.${fmt}`);
    } catch {
      toast.error("Error al exportar");
    } finally {
      setBusy("");
    }
  };

  const dlPdf = async (key) => {
    setBusy(`${key}-pdf`);
    try {
      const { default: axios } = await import("axios");
      const { default: jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const t = localStorage.getItem("ovamx:admin_token");
      const res = await axios.get(`${auth.API}/admin/export/${key}.csv`, { headers: { Authorization: `Bearer ${t}` } });
      const lines = res.data.trim().split("\n").map((l) => l.split(","));
      const head = lines.shift();
      const body = lines.slice(0, 1500); // safety cap for PDF rendering
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text(`Ovamx · ${key}`, 14, 14);
      doc.setFontSize(8);
      doc.text(new Date().toLocaleString("es"), 14, 20);
      autoTable(doc, {
        startY: 26,
        head: [head],
        body,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [14, 22, 38] },
      });
      doc.save(`ovamx_${key}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`PDF generado · ${body.length} filas`);
    } catch {
      toast.error("Error al generar PDF");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="tab-export" className="space-y-6">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Exportación de datos</p>
        <p className="mt-1 text-[11px] text-ink-mid">CSV y Excel se generan en el servidor. PDF en tu navegador (hasta 1.500 filas).</p>
      </div>

      <section className="grid md:grid-cols-2 gap-3">
        {COLLECTIONS.map((c) => (
          <div key={c.id} data-testid={`export-row-${c.id}`} className="border hairline bg-cream-card rounded-2xl p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h4 className="font-serif text-lg tracking-tight">{c.label}</h4>
              <p className="text-[11px] text-ink-mid">{c.desc}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Btn testid={`export-${c.id}-csv`}  onClick={() => dl(c.id, "csv")}  busy={busy === `${c.id}-csv`}  icon={FileText}        label="CSV" />
              <Btn testid={`export-${c.id}-xlsx`} onClick={() => dl(c.id, "xlsx")} busy={busy === `${c.id}-xlsx`} icon={FileSpreadsheet} label="Excel" />
              <Btn testid={`export-${c.id}-pdf`}  onClick={() => dlPdf(c.id)}      busy={busy === `${c.id}-pdf`}  icon={FileDown}        label="PDF" />
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

function Btn({ testid, onClick, busy, icon: Icon, label }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full bg-ink text-cream hover:bg-coral transition disabled:opacity-50"
    >
      <Icon className="w-3.5 h-3.5" /> {busy ? "…" : label}
    </button>
  );
}
